import { initAccessControlListener } from '@bridebook/web/lib/access-control/epics/listener';
import { aiPlannerEpics } from '@bridebook/web/lib/ai-planner/epics';
import { authAnalyticsEpic } from '@bridebook/web/lib/auth/analytics';
import { budgetAnalyticsEpic } from '@bridebook/web/lib/budget/analytics';
import { initBudgetListener } from '@bridebook/web/lib/budget/listener';
import {
  fetchTasksInitialEpic,
  initChecklistAnalyticsEpic,
  initChecklistListener,
} from '@bridebook/web/lib/checklist/listener';
import { IConfigureDeps } from '@bridebook/web/lib/configure-deps';
import { connectedActionsEpic } from '@bridebook/web/lib/connected-actions';
import { triggerSupplierContentTranslationsEpic } from '@bridebook/web/lib/content-translations/epics';
import { datepickerAnalyticsEpics } from '@bridebook/web/lib/datepicker/analytics';
import { closeEnquiryOnEnquiryLimitEpic } from '@bridebook/web/lib/enquiries/actions';
import {
  enquiriesServerSideTrackEpic,
  enquiryFormToggleAnalyticsEpic,
} from '@bridebook/web/lib/enquiries/analytics/epics';
import { initGuestlistListener } from '@bridebook/web/lib/guestlist/listener';
import { getAndUpdateViewerCountryEpic } from '@bridebook/web/lib/i18n/epics';
import { onboardingEpics } from '@bridebook/web/lib/onboarding/epics';
import { initScrapbookListener } from '@bridebook/web/lib/scrapbook/listener';
import {
  changedSearchLocationAnalyticsDebouncedEpic,
  fetchSearchSuggestionsAnalyticsEpic,
  sendAnalyticsOnFetchSearchSuccessEpic,
} from '@bridebook/web/lib/search/analytics';
import * as searchEpics from '@bridebook/web/lib/search/epics';
import {
  mobileAssignmentEpic,
  mobileEnquirySuccessEpic,
  mobileSearchInteractionEpic,
} from '@bridebook/web/lib/task/epics/mobile-today-task';
import { changeWeddingCountryEpic } from '@bridebook/web/lib/settings/epics';
import { shortlistAnalyticsEpic } from '@bridebook/web/lib/shortlist/analytics';
import { initShortlistListener } from '@bridebook/web/lib/shortlist/listener';
import { supplierAnalyticsEpic } from '@bridebook/web/lib/supplier/analytics';
import {
  initActiveTodayTaskDefinition,
  initUserTodayTaskListener,
  initWeddingTodayTaskListener,
} from '@bridebook/web/lib/task/listener';
import { trackCordovaEpic } from '@bridebook/web/lib/track-utils/epics/';
import { IApplicationState, IEpic } from '@bridebook/web/lib/types';
import { initUserListener } from '@bridebook/web/lib/users/listener';
import { initWeddingEventsListener } from '@bridebook/web/lib/wedding-website/events-listener';
import { profileAnalyticsListenerEpic } from '@bridebook/web/lib/weddings/analytics';
import { initWeddingListener } from '@bridebook/web/lib/weddings/listener';
import { values } from 'ramda';
import { AnyAction } from 'redux';
import { ActionsObservable, combineEpics, StateObservable } from 'redux-observable';
import { BehaviorSubject } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import * as accessControlEpics from './access-control/epics';
import {
  fetchGroups,
  globalListenersInitEpic,
  globalListenersStopEpic,
  initDeviceOsEpic,
  navigationEpic,
  qaHelpersEpic,
  routingEpic,
  windowResizeAndroidScrollToInputEpic,
  windowResizeEpic,
} from './app/epics';
import * as authEpics from './auth/epics';
import { bbCommonEpic } from './bbcommon/actions';
import {
  closeTaskDrawerEpic,
  initialiseChecklistEpic,
  setTaskDoneByIdEpic,
  updateGroupEpic,
  updateTasksEpic,
  updateTasksTotalsOnWeddingEpic,
} from './checklist/epics';
import { updateWeddingWithEnquiryFieldsEpic } from './enquiries/actions';
import { fetchEnquiryConfirmationSupplierEpic, fetchEnquiryDatesEpic } from './enquiries/epics';
import {
  appsFlyerPBTracking,
  appUrlOpenEpic,
  backButtonEpic,
  getAppBuildInfoEpic,
  hideSplashScreenEpic,
  mobileNativeKeyboardEpic,
  pushNotificationsSaveDeviceTokenEpics,
  registerPushNotificationEpic,
  registerPushNotificationErrorEpic,
  registerPushNotificationsListenerEpic,
  registerTalkJsDeviceEpic,
  removePushNotificationTokenOnSignOutEpic,
  requestPushNotificationPermissionsOptInEpic,
  scheduleRegisterTalkJsDeviceEpic,
  showAppRatePopupEpic,
  showPushNotificationOptInPopupEpic,
} from './mobile-app/epics';
import {
  closeAddSupplierModalsEpic,
  fetchShortlistedSuppliersEpic,
  setSupplierNoteByIdEpic,
  shortlistBookSupplierEpic,
  shortlistBookSupplierUpdateLocationEpic,
  shortlistUnbookSupplierEpic,
  unbookAllVenuesEpic,
  updateShortlistedSupplierEpic,
  viewedShortlistEpic,
} from './shortlist/epics';
import {
  createCustomSupplierEpic,
  customSupplierSuccessEpic,
  validateFormEpics,
} from './shortlist/epics-custom-supplier';
import { registerSupplierCardInView } from './supplier/epics';
import uiEpics from './ui/epics';
import { debouncedGoBackHomeEpic } from './ui/epics/debounced-go-back-home-epic';
import * as userEpics from './users/epics';
import {
  confirmOrDenySupplierInterestEpic,
  onVenueBookedConfirmedShowDiscoverPhotographers,
  onVenueBookedSetDiscoverPhotographers,
  openSupplierInterestConfirmationEpic,
  openSupplierMultiEnquiryPopupOnEnquiryUpdate,
  openVenueConfirmationPopup,
  openVenuePopupAfterTimePeriod,
  openVenuePopupFromUrlParam,
  openVenuePopupOnBudgetChange,
  openVenuePopupOnChecklistMainTaskChange,
  openVenuePopupOnChecklistSubtaskChange,
  openVenuePopupOnGuestlistExport,
  openVenuePopupOnMarkedTwoNonVenueAsBooked,
  recommendedSuppliers,
  venueRecommendedSuppliers,
} from './venue-confirmation/epics';
import {
  fetchCountryBoundsEpic,
  initInboxListenerEpic,
  syncBookedVenueToWebsiteEpic,
  updateBookingsVenueEpic,
  updatePublishedWebsiteSectionsEpic,
  changeCurrencyEpic,
  updateWeddingDraftDate,
  updateWeddingDraftPartners,
  updateWeddingDraftWebsiteConfig,
  updateWeddingDraftWebsiteSection,
  updateWeddingFieldEpic,
  updateWeddingPreferencesEpic,
  setWeddingDateEpic,
  weddingProfileSaveDateEpics,
} from './weddings/epics';
import { createMainEventIfNotExistsEpic } from './weddings/epics/update-wedding';

import updateGuestsMissingEmailEpic from './guestlist/epics/update-guests-missing-email';

const globalEpics: IEpic[] = values({
  // app
  trackCordovaEpic,
  windowResizeEpic,
  windowResizeAndroidScrollToInputEpic,
  routingEpic,
  navigationEpic,
  fetchGroups,
  globalListenersInitEpic,
  globalListenersStopEpic,
  initDeviceOsEpic,
  // i18n
  getAndUpdateViewerCountryEpic,
  // mobileapp
  pushNotificationsSaveDeviceTokenEpics,
  scheduleRegisterTalkJsDeviceEpic,
  registerTalkJsDeviceEpic,
  showAppRatePopupEpic,
  mobileNativeKeyboardEpic,
  hideSplashScreenEpic,
  showPushNotificationOptInPopupEpic,
  registerPushNotificationEpic,
  registerPushNotificationErrorEpic,
  registerPushNotificationsListenerEpic,
  requestPushNotificationPermissionsOptInEpic,
  removePushNotificationTokenOnSignOutEpic,
  backButtonEpic,
  appUrlOpenEpic,
  appsFlyerPBTracking,
  getAppBuildInfoEpic,
  // searchScrollEpic,
  qaHelpersEpic,
  // auth
  ...authEpics,
  // ACL
  ...accessControlEpics,
  // users
  ...userEpics,
  shortlistBookSupplierUpdateLocationEpic,
  initUserListener,
  // // enquiries
  closeEnquiryOnEnquiryLimitEpic,
  fetchEnquiryConfirmationSupplierEpic,
  fetchEnquiryDatesEpic,
  mobileEnquirySuccessEpic,
  updateWeddingWithEnquiryFieldsEpic,
  // enquiries analytics
  enquiryFormToggleAnalyticsEpic,
  enquiriesServerSideTrackEpic,
  // // search
  ...searchEpics,
  mobileSearchInteractionEpic,
  // guestlist
  initGuestlistListener,
  // shortlist
  initShortlistListener,
  createCustomSupplierEpic,
  customSupplierSuccessEpic,
  validateFormEpics,
  shortlistBookSupplierEpic,
  shortlistUnbookSupplierEpic,
  viewedShortlistEpic,
  unbookAllVenuesEpic,
  closeAddSupplierModalsEpic,
  fetchShortlistedSuppliersEpic,
  updateShortlistedSupplierEpic,
  setSupplierNoteByIdEpic,
  // checklist
  fetchTasksInitialEpic,
  initChecklistListener,
  initChecklistAnalyticsEpic,
  setTaskDoneByIdEpic,
  updateGroupEpic,
  updateTasksEpic,
  closeTaskDrawerEpic,
  updateTasksTotalsOnWeddingEpic,
  initBudgetListener,
  debouncedGoBackHomeEpic,
  ...uiEpics,
  // datepicker
  // profile
  // weddingProfileListenerEpic,
  updateWeddingFieldEpic,
  setWeddingDateEpic,
  changeCurrencyEpic,
  updateWeddingDateEpic: createMainEventIfNotExistsEpic,
  updateWeddingPreferencesEpic,
  updateBookingsVenueEpic,
  weddingProfileSaveDateEpics,
  //wedding website
  initWeddingEventsListener,
  syncBookedVenueToWebsiteEpic,
  updatePublishedWebsiteSectionsEpic,
  fetchCountryBoundsEpic,
  initInboxListenerEpic,
  updateWeddingDraftWebsiteSection,
  updateWeddingDraftWebsiteConfig,
  updateWeddingDraftPartners,
  updateWeddingDraftDate,
  // analytics
  profileAnalyticsListenerEpic,
  authAnalyticsEpic,
  shortlistAnalyticsEpic,
  supplierAnalyticsEpic,
  datepickerAnalyticsEpics,
  budgetAnalyticsEpic,
  sendAnalyticsOnFetchSearchSuccessEpic,
  changedSearchLocationAnalyticsDebouncedEpic,
  fetchSearchSuggestionsAnalyticsEpic,
  registerSupplierCardInView,
  bbCommonEpic,
  // Settings
  changeWeddingCountryEpic,
  // Scrapbook
  initScrapbookListener,
  initWeddingListener,
  initAccessControlListener,
  // Connected actions
  connectedActionsEpic,
  // venuerexModalEpic,
  // countToVenuerexModalEpic,
  //  Venue Confirmation popup
  openVenuePopupOnBudgetChange,
  openVenuePopupOnChecklistMainTaskChange,
  openVenuePopupOnChecklistSubtaskChange,
  openVenuePopupOnGuestlistExport,
  openVenuePopupOnMarkedTwoNonVenueAsBooked,
  onVenueBookedSetDiscoverPhotographers,
  openSupplierInterestConfirmationEpic,
  confirmOrDenySupplierInterestEpic,
  onVenueBookedConfirmedShowDiscoverPhotographers,
  openVenuePopupAfterTimePeriod,
  openVenuePopupFromUrlParam,
  openSupplierMultiEnquiryPopupOnEnquiryUpdate,
  openVenueConfirmationPopup,
  venueRecommendedSuppliers,
  recommendedSuppliers,
  // Onboarding
  onboardingEpics,
  //task
  initWeddingTodayTaskListener,
  initUserTodayTaskListener,
  initActiveTodayTaskDefinition,
  mobileAssignmentEpic,
  //checklist
  initialiseChecklistEpic,
  // content translations
  triggerSupplierContentTranslationsEpic,
  // guestlist
  updateGuestsMissingEmailEpic,
  // ai planner
  // After the app epics, so restore sees the screen size the resize epic settled.
  aiPlannerEpics,
}).filter((item): item is IEpic => typeof item === 'function');

let epic$: BehaviorSubject<IEpic> | null;

const configureEpics =
  (deps: IConfigureDeps) =>
  (action$: ActionsObservable<AnyAction>, state$: StateObservable<IApplicationState>) => {
    // https://github.com/redux-observable/redux-observable/blob/master/docs/recipes/AddingNewEpicsAsynchronously.md
    // add BehaviorSubject for lazy load of epics
    if (!epic$) {
      epic$ = new BehaviorSubject(combineEpics(...globalEpics));
    }
    // @ts-ignore FIXME
    return epic$.pipe(mergeMap((epic) => epic(action$, { ...deps, state$ })));
  };

export const getEpic = () => epic$;

export default configureEpics;
